// TO DO: Remove this duplication with api grfq type
export type Source = 'BIT' | 'DBT' | 'BYB' | 'PRDX' | 'BLSH';

export interface GrfqVenue {
  /** Venue acronym used in most API req/resp bodies */
  readonly code: Source;
  /**
   * Venue acronym used for display purposes. Usually follows `code`.
   * `null` means that the code is not to be displayed.
   */
  readonly displayCode: string | null;
  /** Human-readable name */
  readonly name: string;
  readonly shortName: string;
  readonly custom: boolean;
  readonly minBlockSizeCalculation: 'leg' | 'strategy';
  /** Normalization value for min block size calculation. Use 0 for no normalization. */
  readonly normalizedMinBlockSize: number;
  /**
   * Unit `minBlockSize` is denominated in. 'coin' everywhere except
   * Deribit, which publishes a quote-currency notional for its
   * USDC-linear futures (SF: 50,000 USDC) and coins for everything
   * else. Read it through `minBlockSizeIsQuoteNotional`
   * (`#/products/domain`), which combines it with the product shape.
   */
  readonly minBlockSizeUnit: 'coin' | 'quote-notional-linear-future';
  /**
   * Whether the venue's block minimum applies per strategy UNIT, so a
   * multi-leg strategy divides it by the sum of the legs' absolute
   * ratios. Only Deribit does this (`get_min_order_size`); every
   * other venue returns its minimum raw.
   *
   * Independent of `minBlockSizeUnit` -- it answers a different
   * question. A notional minimum is never divided here: the coin
   * conversion in `minQuantityForProduct` already accounts for every
   * leg, so doing both puts a spread below the real floor.
   */
  readonly minBlockSizePerStrategyUnit: boolean;
}

export const VENUES: readonly GrfqVenue[] = [
  {
    code: 'DBT',
    displayCode: 'DBT',
    name: 'Deribit (DBT)',
    shortName: 'Deribit',
    custom: true,
    minBlockSizeCalculation: 'strategy',
    normalizedMinBlockSize: 0,
    minBlockSizeUnit: 'quote-notional-linear-future',
    minBlockSizePerStrategyUnit: true,
  },
  {
    code: 'PRDX',
    displayCode: 'PRDX',
    name: 'Paradex (PRDX)',
    shortName: 'Paradex',
    custom: true,
    minBlockSizeCalculation: 'leg',
    normalizedMinBlockSize: 0,
    minBlockSizeUnit: 'coin',
    minBlockSizePerStrategyUnit: false,
  },
  {
    code: 'BLSH',
    displayCode: 'BLSH',
    name: 'Bullish (BLSH)',
    shortName: 'Bullish',
    custom: true,
    minBlockSizeCalculation: 'leg',
    normalizedMinBlockSize: 0,
    minBlockSizeUnit: 'coin',
    minBlockSizePerStrategyUnit: false,
  },
  {
    code: 'BYB',
    displayCode: 'BYB',
    name: 'Bybit (BYB)',
    shortName: 'Bybit',
    custom: true,
    minBlockSizeCalculation: 'strategy',
    normalizedMinBlockSize: 1,
    minBlockSizeUnit: 'coin',
    minBlockSizePerStrategyUnit: false,
  },
  // Bit.com is being deprecated (decommissioned: no discovery job or
  // marketdata). Commented out to drop it from the venue selectors while
  // keeping it easy to restore; the 'BIT' code stays in the Source type so
  // historical references still type-check.
  // {
  //   code: 'BIT',
  //   displayCode: 'BIT',
  //   name: 'Bit.com (BIT)',
  //   shortName: 'Bit.com',
  //   custom: true,
  //   minBlockSizeCalculation: 'leg',
  //   normalizedMinBlockSize: 0,
  //   minBlockSizeUnit: 'coin',
  //   minBlockSizePerStrategyUnit: false,
  // },
];

export const getVenues = () => VENUES;

// Bullish is shipped to every environment's bundle and is now live in
// staging, testnet and production. getVenues() stays unfiltered so by-code
// lookups (getVenue) still resolve BLSH; the selector gate stays in place
// (other unlaunched venues could reuse it).
export const isVenueVisible = (code: Source): boolean =>
  code !== 'BLSH' ||
  ENVIRONMENT === 'stage' ||
  ENVIRONMENT === 'test' ||
  ENVIRONMENT === 'production';

export const getSelectableVenues = (): readonly GrfqVenue[] =>
  VENUES.filter((venue) => isVenueVisible(venue.code));

/**
 * Venue a selector opens on: the first selectable venue the user's firm is
 * entitled to (`user.active_products`, a list of venue codes). Defaulting to
 * a fixed venue instead would show a name the user cannot trade and which is
 * missing from the entitlement-filtered options.
 *
 * `undefined` when the entitlements name no selectable venue -- an empty list
 * (the auth reducer's initial value, so also before the user info lands) or
 * only codes absent from `VENUES`, such as the deprecated `BIT`. There is no
 * venue to open on then. A caller that has to hold a code either way (the
 * builder does: `venueView` throws on a venue outside `VENUES`) must keep
 * that placeholder out of the UI rather than render it as a selection the
 * user cannot change -- see `VenueSelector`.
 */
export const getDefaultVenueCode = (
  activeVenueCodes: readonly string[],
): Source | undefined =>
  getSelectableVenues().find((venue) => activeVenueCodes.includes(venue.code))
    ?.code;

export const venueSupportsCustomStrategy = (code: GrfqVenue['code']) => {
  return getVenue(code).custom;
};

/**
 * Venue by code, or `undefined` when the code is not in `VENUES`.
 *
 * Products outlive their venue row -- `BIT` is commented out of
 * `VENUES` while `products/config.ts` still carries its products, and
 * `products/loader.ts` injects future-spread products with venues of
 * their own. Predicates that only need a venue POLICY use this and
 * fall back to the default, rather than throwing mid-render.
 */
export const findVenue = (code: string): GrfqVenue | undefined =>
  getVenues().find((vn) => vn.code === code);

export const getVenue = (code: string) => {
  const venue = findVenue(code);
  if (venue === undefined)
    throw new Error(`Venue with code "${code}" doesn't exist.`);
  return venue;
};
